import classNames from "classnames";
import get from "lodash/get";
import PropTypes from "prop-types";
import React from "react";
import {
  Button,
  Col,
  FormLabel,
  FormControl,
  FormGroup,
  FormText,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { Notification } from "ui/notification";
import { getErrorMessage } from "util/response";
import zxcvbn from "zxcvbn";

class RegistrationPage extends React.PureComponent {
  static get propTypes() {
    return {
      inviteDetails: PropTypes.func.isRequired,
      inviteId: PropTypes.string.isRequired,
      register: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      errorMessage: "",
      invitation: {
        email: "",
      },
      name: "",
      p1: "",
      p2: "",
      score: 0,
      showScore: false,
      submitted: false,
      successMessage: "",
      valid: false,
    };

    this._submit = this._submit.bind(this);
    this._passChanged = this._passChanged.bind(this);
    this._nameChanged = this._nameChanged.bind(this);
  }

  componentDidMount() {
    this.props
      .inviteDetails(this.props.inviteId)
      .then((resp) => {
        const invitation = get(resp, "data.data", null);

        if (invitation) {
          this.setState({
            errorMessage: "",
            invitation,
            name: get(invitation, "name", ""),
            successMessage: "",
          });
        } else {
          this.setState({
            errorMessage:
              "Failed to retrieve the invitation. Please try again later.",
            successMessage: "",
          });
        }
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      });
  }

  _submit() {
    this.props
      .register(
        this.props.inviteId,
        this.state.name,
        this.state.p1,
        this.state.p2,
      )
      .then(() => {
        this.setState({
          errorMessage: "",
          successMessage: "Your account was created successfully.",
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({
          submitted: false,
        });
      });

    this.setState({ submitted: true });
  }

  _isValid(n, p1, p2) {
    return n && p1 && p2 && p1 === p2 && p1.length >= 8;
  }

  _nameChanged(e) {
    const n = e.target.value || "";
    this.setState({
      name: n,
      valid: this._isValid(n, this.state.p1, this.state.p2),
    });
  }

  _passChanged() {
    const p1 = this.password1.value;
    const p2 = this.password2.value;
    const zp1 = zxcvbn(p1);

    this.setState({
      p1,
      p2,
      valid: this._isValid(this.state.name, p1, p2),
      score: zp1.score,
      showScore: p1.length > 0,
    });
  }

  _renderPasswordScore() {
    if (!this.state.showScore) {
      return null;
    }

    const classes = classNames({
      "progress-bar": true,
      "progress-bar-striped": true,
      "progress-bar-success": this.state.score === 4,
      "progress-bar-warning": this.state.score >= 2 && this.state.score < 4,
      "progress-bar-danger": this.state.score <= 1,
    });

    const style = {
      width: `${this.state.score * 20 + 20}%`,
    };

    return (
      <div className="progress progress-sm active">
        <div
          className={classes}
          style={style}
          aria-valuenow="40"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    );
  }

  render() {
    const { invitation } = this.state;
    let accountAccess = "";
    if (invitation) {
      const role = invitation.role === "R" ? "Read only" : "Full access";

      accountAccess = `${invitation.account_name} / ${role}`;
    }

    return (
      <div className="registration-page">
        <div className="header">
          <h1>CORTEX</h1>
        </div>
        <div className="registration-box">
          <div className="registration-box-body">
            <Notification
              error={this.state.errorMessage}
              success={this.state.successMessage}
              successChildren={<Link to="/login">Login</Link>}
            />
            <Row>
              <Col xs={12}>
                <FormGroup controlId="email">
                  <FormLabel>Email</FormLabel>
                  <FormControl
                    type="text"
                    value={this.state.invitation.email}
                    disabled
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup controlId="account">
                  <FormLabel>Account / Access</FormLabel>
                  <FormControl
                    type="text"
                    value={accountAccess}
                    disabled
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup controlId="name">
                  <FormLabel>Name</FormLabel>
                  <FormControl
                    type="text"
                    value={this.state.name}
                    onChange={this._nameChanged}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup controlId="password">
                  <FormLabel>Password</FormLabel>
                  <FormControl
                    type="password"
                    ref={(c) => {
                      this.password1 = c;
                    }}
                    onChange={this._passChanged}
                    required
                  />
                  <FormText>
                    Required. A strong password combines letters, numbers and
                    symbol characters. Your password must be at least 8
                    characters long.
                  </FormText>
                  {this._renderPasswordScore()}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup controlId="retypePassword">
                  <FormLabel>Retype Password</FormLabel>
                  <FormControl
                    type="password"
                    ref={(c) => {
                      this.password2 = c;
                    }}
                    onChange={this._passChanged}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Button
                  variant="primary"
                  onClick={this._submit}
                  disabled={this.state.submitted || !this.state.valid}
                  className="registration-button"
                >
                  Register
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }
}

export { RegistrationPage };
